import {
  Button,
  DatePicker,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
} from "components";
import { useAuth } from "contexts/AuthContext";
import { ListContext } from "contexts/ListContext";
import { ChevronDown } from "lucide-react";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactSelect from "react-select";
import {
  getPatientsCSV,
  getPatientsXLS,
  getPatientsJSON,
} from "services/patients";

import cn from "classnames";
import { getStoredFilters } from "@utils/filters";
import { PATIENT_KEY } from "@utils/constants";
import InstitutionCodeSelector from "@components/institutions/InstitutionCodeSelector";
import SubtypeSelector from "../PatientsTable/SubtypeSelector";
import DxSelector from "../PatientsTable/DxSelector";
export const PatientsHeader = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const { filter, setFilter } = useContext<any>(ListContext);
  const storedValue = getStoredFilters(PATIENT_KEY);

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: "36px",
      height: "36px",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      opacity: 0.7,
      fontSize: "12px",
    }),
    singleValue: (provided: any, state: any) => ({
      ...provided,
      opacity: 0.7,
    }),
    valueContainer: (provided: any, state: any) => ({
      ...provided,
      padding: "0 6px",
      position: "absolute",
      top: "5px",
    }),
    input: (provided: any, state: any) => ({
      ...provided,
      margin: "0px",
    }),
    indicatorSeparator: (state: any) => ({
      display: "none",
    }),
    indicatorsContainer: (provided: any, state: any) => ({
      ...provided,
      height: "36px",
      position: "absolute",
      right: 0,
      top: 0,
    }),
  };

  const relapseOptions = [
    { value: "all", label: t("all") },
    { value: "true", label: t("yes") },
    { value: "false", label: t("no") },
  ];

  const defaultRelapse = relapseOptions.find(
    (option) => option.value === storedValue.relapse
  );

  return (
    <div className="w-full mb-4">
      <div className="mr-auto">
        <p className="text-lg lg:w-[9rem] mb-4 font-medium">
          {t("myPatients")}
        </p>
      </div>

      <div className="flex flex-row justify-between w-full gap-2">
        <div
          className={cn(
            "flex flex-row justify-between w-fit gap-2",
            !user.isAdmin && "align-center"
          )}
        >
          {user.isAdmin && (
            <>
              <InstitutionCodeSelector
                className=""
                onChange={(value: any) =>
                  setFilter({
                    ...filter,
                    institutionCode: value === "all" ? undefined : value,
                  })
                }
                customStyles={customStyles}
              />

              <SubtypeSelector
                className=""
                onChange={(value: any) =>
                  setFilter({
                    ...filter,
                    subtype: value === "all" ? undefined : value,
                  })
                }
                customStyles={customStyles}
              />

              <DxSelector
                className=""
                onChange={(value: any) =>
                  setFilter({
                    ...filter,
                    dx: value === "all" ? undefined : value,
                  })
                }
                customStyles={customStyles}
              />
            </>
          )}

          <div className={cn("w-fit bg-red-500", !user.isAdmin && "flex justify-start")}>
            <ReactSelect
              className={cn("select", " h-[36px] w-[120px] 2xl:w-[170px] py-0")}
              options={relapseOptions}
              styles={customStyles}
              placeholder={defaultRelapse?.label || "Recaída"}
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary: "#ED5939",
                  primary25: "#F4F4F4",
                },
              })}
              onChange={({ value }: any) =>
                setFilter({
                  ...filter,
                  relapse: value != "all" ? value : undefined,
                })
              }
            />
          </div>
        </div>

        <div className="w-full flex flex-row items-center gap-4 2xl:justify-start">
          <DatePicker
            onChange={(date) => {
              setFilter({
                ...filter,
                obito: date
                  ? `${date.from?.toISOString()},${date?.to?.toISOString()}`
                  : undefined,
              });
            }}
            btnClass="h-[36px] !w-[180px]"
          />

          <div className="mr-2 w-full xl:w-full">
            <Input
              className="w-full"
              placeholder="Buscar paciente"
              inputClasses="h-[36px] hover:border-[#b3b3b3] w-full text-xs 2xl:text-sm"
              icon="Search"
              defaultValue={storedValue.text}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFilter({ ...filter, text: e?.target?.value });
              }}
            />
          </div>
        </div>
        {user.isAdmin && (
          <div className="">
            <DropdownMenu open={open} onOpenChange={setOpen}>
              <DropdownMenuTrigger>
                <Button
                  background
                  textColor="white"
                  type="link"
                  className="!py-2 h-[36px] w-[110px]"
                >
                  Export
                  <ChevronDown
                    className={`inline-flex text-sm ml-1 ${
                      open && "rotate-180"
                    } rotate transition-all transform`}
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="ml-2 min-w-[7rem]">
                <DropdownMenuItem
                  className="2xl:px-3 flex items-center justify-center"
                  onClick={() => getPatientsCSV(filter)}
                >
                  <span>CSV file</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="2xl:px-3 flex items-center justify-center"
                  onClick={() => getPatientsXLS(filter)}
                >
                  <span>XLS file</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="2xl:px-3 flex items-center justify-center"
                  onClick={() => getPatientsJSON(filter)}
                >
                  <span>JSON file</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {!user.isAdmin && (
          <Button
            background
            textColor="white"
            type="link"
            className="w-[350px] ml-2 !py-2 !px-3"
            to="/new-patient"
          >
            + {t("newPatient")}
          </Button>
        )}
      </div>
    </div>
  );
};
